import { useState, useEffect } from 'react';
import { getSDK } from '@/store/sdkStore';
import { Button } from '@/components/ui/button';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { friendlyError } from '@/lib/errors';
import { useColumnWidths } from '@/lib/useColumnWidths';
import { createLogger } from '@/lib/logger';

const log = createLogger('TasksLinkedTab');

interface TasksLinkedTabProps {
  subjectType: string;
  subjectTable: string;
  subjectId: string;
  subjectPresentation?: string;
}

interface TaskRowView {
  id: string;
  name: string;
  description: string;
  status: string;
  assignee: string;
}

/** Колонки таблицы задач (порядок/подписи как были) */
const COLUMNS: Array<{ field: 'name' | 'description' | 'status' | 'assignee'; label: string }> = [
  { field: 'name', label: 'Тема' },
  { field: 'description', label: 'Описание' },
  { field: 'status', label: 'Статус' },
  { field: 'assignee', label: 'Исполнитель' },
];

/** Дефолтные ширины колонок (px) — по имени колонки (единый механизм useColumnWidths) */
const COLUMN_DEFAULT_WIDTHS: Record<string, number> = {
  name: 180,
  description: 240,
  status: 110,
  assignee: 130,
};

/**
 * Вкладка «Задачи» у объекта (ЕДИНЫЙ механизм вместо старого TasksPage):
 * список задач по предмету (sdk.task.listTasks — маскирование/права на
 * сервере, SSoT); клик по строке → AutoObjectForm (та же форма, что везде);
 * «+ Создать» → AutoObjectForm новой задачи с prefill предмета.
 */
export function TasksLinkedTab({ subjectType, subjectTable, subjectId, subjectPresentation }: TasksLinkedTabProps) {
  const [rows, setRows] = useState<TaskRowView[]>([]);
  const [taskType, setTaskType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openTask, setOpenTask] = useState<{ id?: string } | null>(null);

  // Ширина колонок таблицы — ЕДИНЫЙ механизм (useColumnWidths): перетаскивание
  // правой границы заголовка (мышь/тач); сохраняется per-пользователь в
  // настройках (SSoT — сервер). Ключ уникален для списка задач по предмету.
  const { widths, startResize } = useColumnWidths(`tasksLinked:${subjectType}:${subjectTable}`, COLUMN_DEFAULT_WIDTHS);

  useEffect(() => {
    // Код типа задач (SSoT — metadata_task_types): единственный/первый
    void (async () => {
      try {
        const tt: any = await getSDK().task.listTaskTypes({});
        setTaskType(tt?.items?.[0]?.code || '');
      } catch (e) {
        log.error('listTaskTypes failed', e);
      }
    })();
  }, []);

  const load = async () => {
    if (!subjectId) return;
    setLoading(true);
    setError(null);
    try {
      const res: any = await getSDK().task.listTasks({
        subjectType,
        subjectTable,
        subjectId,
        // ИДЕНТИЧНО дин.списку «Задачи по …» (listTableData по subject_id):
        // показываем ВСЕ задачи предмета, включая выполненные/отменённые.
        includeDone: true,
        pageSize: 200,
      } as any);
      setRows(((res?.items || []) as any[]).map((t) => ({
        id: t.id || '',
        name: t.name || '',
        // Описание задачи (из заявки — многострочное) — колонка в таблице.
        description: t.description || '',
        status: t.statusName || t.statusCode || '',
        assignee: t.assigneeName || '',
      })));
    } catch (e: any) {
      setError(friendlyError(e));
      log.error('load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectType, subjectTable, subjectId]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {subjectPresentation ? `Предмет: ${subjectPresentation}` : ''}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          disabled={!subjectId || !taskType}
          title={!taskType ? 'Не найдено ни одного типа задач' : ''}
          onClick={() => setOpenTask({})}
        >
          + Создать задачу
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground p-2">Загрузка…</div>
      ) : error ? (
        <div className="text-xs text-destructive p-2">{error}</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground p-2 border rounded bg-muted/20">Задач нет</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-xs border rounded-md overflow-hidden">
            <thead>
              <tr className="border-b bg-muted/40">
                {COLUMNS.map((c) => (
                  <th key={c.field} className="relative px-2 py-1 text-left font-medium" style={{ width: widths[c.field] }}>
                    {c.label}
                    {/* Ручка ширины: перетаскивание правой границы заголовка */}
                    <span
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none"
                      onPointerDown={(e) => startResize(c.field, e)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0 hover:bg-muted/30 cursor-pointer" onClick={() => setOpenTask({ id: r.id })}>
                  <td className="px-2 py-1" style={{ width: widths.name }}>{r.name}</td>
                  <td className="px-2 py-1" style={{ width: widths.description }}>
                    {/* Описание: одна строка с усечением, полный текст — в подсказке (title) */}
                    <span className="block truncate" title={r.description}>{r.description}</span>
                  </td>
                  <td className="px-2 py-1" style={{ width: widths.status }}>{r.status}</td>
                  <td className="px-2 py-1" style={{ width: widths.assignee }}>{r.assignee}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openTask && taskType && (
        <AutoObjectForm
          objectType="task"
          objectName={taskType}
          objectId={openTask.id}
          initialData={openTask.id ? undefined : { subject_id: subjectId, subject_id_table: subjectTable }}
          onClose={() => { setOpenTask(null); void load(); }}
        />
      )}
    </div>
  );
}
